import { useCallback, useEffect, useRef } from "react";
import useGameSounds from "../../../../hooks/useGameSounds";
import { playSound } from "../../../../utils/gameAudio";
import { HOL_HOT_SEAT_CLAP_URL } from "../../../../constants/holDefaultSounds";

/**
 * Play the same Beerillioner cue sounds on the admin host as on the player screen.
 */
export default function useBeerillionerHostSounds(session) {
  const {
    play,
    playLoop,
    stopBgMusic,
    loaded: soundsLoaded,
    sounds,
  } = useGameSounds("beerillioner");

  const playRef = useRef(play);
  const playLoopRef = useRef(playLoop);
  const soundsRef = useRef(sounds);
  const sessionStatusRef = useRef(null);
  const fffTimerEndPlayedRef = useRef(false);
  const fffLeaderboardVisibleRef = useRef(false);
  /** Manual "Intro Sound" preview while no session is active. */
  const introPreviewRef = useRef(false);
  /** Skip idle BG restart when onSessionCreated already started it (avoids clash). */
  const skipIdleBgOnceRef = useRef(false);

  useEffect(() => {
    playRef.current = play;
  }, [play]);

  useEffect(() => {
    playLoopRef.current = playLoop;
  }, [playLoop]);

  useEffect(() => {
    soundsRef.current = sounds;
  }, [sounds]);

  useEffect(() => {
    sessionStatusRef.current = session?.status || null;
  }, [session?.status]);

  useEffect(() => {
    if (!soundsLoaded) return;
    if (!session || session.status === "ended") {
      // Don't kill a host-triggered Intro Sound preview on effect re-runs.
      if (!introPreviewRef.current) stopBgMusic();
    } else if (session.status === "idle") {
      if (skipIdleBgOnceRef.current) {
        skipIdleBgOnceRef.current = false;
        return;
      }
      playLoop("bg_music_2");
    } else if (session.status === "fff_intro") {
      if (sounds.fff_intro) playLoop("fff_intro");
      else playLoop("bg_music_2");
    }
  }, [
    session?.id,
    session?.status,
    soundsLoaded,
    stopBgMusic,
    playLoop,
    sounds.fff_intro,
  ]);

  const startIntroLoop = useCallback(() => {
    if (soundsRef.current?.fff_intro?.audio_url) {
      playLoopRef.current("fff_intro");
    } else {
      playLoopRef.current("bg_music_2");
    }
  }, []);

  const playIntroSound = useCallback(() => {
    introPreviewRef.current = true;
    if (soundsRef.current?.first_intro_bg?.audio_url) {
      playLoopRef.current("first_intro_bg");
    } else {
      startIntroLoop();
    }
  }, [startIntroLoop]);

  const onSessionCreated = useCallback(() => {
    // Clear intro preview first so clap + get-ready never stack on top of it.
    introPreviewRef.current = false;
    skipIdleBgOnceRef.current = true;
    sessionStatusRef.current = "idle";
    stopBgMusic();
    playRef.current("session_start_clap");
    playLoopRef.current("bg_music_2");
  }, [stopBgMusic]);

  const onFffIntro = useCallback(() => {
    introPreviewRef.current = false;
    sessionStatusRef.current = "fff_intro";
    startIntroLoop();
  }, [startIntroLoop]);

  const onFffStarted = useCallback(() => {
    sessionStatusRef.current = "fff";
    fffTimerEndPlayedRef.current = false;
    playLoopRef.current("fff_question_reading");
  }, []);

  const onAllOptionsShown = useCallback(() => {
    if (sessionStatusRef.current === "fff") playRef.current("show_all_options");
  }, []);

  const onOptionShown = useCallback(() => {
    if (sessionStatusRef.current === "fff") playRef.current("show_option");
  }, []);

  const onFffRoundEnded = useCallback(() => {
    if (fffTimerEndPlayedRef.current) return;
    fffTimerEndPlayedRef.current = true;
    playRef.current("fff_timer_end");
    stopBgMusic();
  }, [stopBgMusic]);

  const onFffLeaderboardToggled = useCallback((visible) => {
    const wasVisible = fffLeaderboardVisibleRef.current;
    fffLeaderboardVisibleRef.current = !!visible;
    if (visible && !wasVisible) {
      playLoopRef.current("fff_leaderboard");
    } else if (!visible && sessionStatusRef.current === "fff") {
      stopBgMusic();
    }
  }, [stopBgMusic]);

  const onFffTimerStarted = useCallback(() => {
    fffTimerEndPlayedRef.current = false;
    playLoopRef.current("fff_timer_bg");
  }, []);

  const onHotSeatSelected = useCallback(() => {
    sessionStatusRef.current = "main_game";
    if (soundsRef.current?.fff_hot_seat?.audio_url) {
      playRef.current("fff_hot_seat");
    } else {
      playSound(HOL_HOT_SEAT_CLAP_URL, { volume: 1, loop: false });
    }
    playRef.current("mg_intro");
    stopBgMusic();
  }, [stopBgMusic]);

  const onQuestionPushed = useCallback(() => {
    playLoopRef.current("mg_bg");
  }, []);

  const onAnswerHighlighted = useCallback(() => {
    if (sessionStatusRef.current === "main_game") {
      playRef.current("mg_lock");
      stopBgMusic();
    }
  }, [stopBgMusic]);

  const onAnswerRevealed = useCallback((isCorrect) => {
    stopBgMusic();
    if (isCorrect) playRef.current("correct");
    else playRef.current("wrong");
  }, [stopBgMusic]);

  const onGameOver = useCallback((reason) => {
    stopBgMusic();
    if (reason === "won") playRef.current("win");
    else if (reason === "timer") playRef.current("wrong");
  }, [stopBgMusic]);

  /** Stop BG and clear intro-preview flag (used when Start New Game is clicked). */
  const stopHostBg = useCallback(() => {
    introPreviewRef.current = false;
    stopBgMusic();
  }, [stopBgMusic]);

  return {
    playIntroSound,
    stopBgMusic: stopHostBg,
    onSessionCreated,
    onFffIntro,
    onFffStarted,
    onAllOptionsShown,
    onOptionShown,
    onFffRoundEnded,
    onFffLeaderboardToggled,
    onFffTimerStarted,
    onHotSeatSelected,
    onQuestionPushed,
    onAnswerHighlighted,
    onAnswerRevealed,
    onGameOver,
  };
}
